import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { commonErrorMessages } from '../../messages';
import { ApiRequestStatus } from '../../model/api-request';
import { ErrorMessages } from '../../model/error';
import signatureAreaRepository from '../../repositories/signature-area/signature-area-repository';
import { AsyncThunkConfig } from '../async-thunk-config';
import { RootState } from '../root-store';

export interface SignRoomState {
  bundleUrlToSign?: string;
  bundleUrlToSignRequestStatus: ApiRequestStatus;
  bundleUrlToSignError?: SignRoomError;
}

const initialState: SignRoomState = {
  bundleUrlToSignRequestStatus: 'idle',
};

const signRoomSlice = createSlice({
  name: 'data/sign-room',
  initialState,
  reducers: {
    setBundleUrlToSign(state, action: PayloadAction<string>) {
      state.bundleUrlToSign = action.payload;
      state.bundleUrlToSignRequestStatus = 'succeeded';
    },
    clearBundleUrlToSignError(state) {
      state.bundleUrlToSignError = undefined;
      state.bundleUrlToSignRequestStatus = 'idle';
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(
      retrieveBundleUrlForSignature.fulfilled,
      (state, action) => {
        state.bundleUrlToSignRequestStatus = 'succeeded';
        state.bundleUrlToSign = action.payload;
        state.bundleUrlToSignError = undefined;
      }
    );

    builder.addCase(retrieveBundleUrlForSignature.rejected, (state, action) => {
      state.bundleUrlToSignRequestStatus = 'failed';
      state.bundleUrlToSignError = action.error.message as SignRoomError;
    });
  },
});

export const signRoomSliceActions = signRoomSlice.actions;

export default signRoomSlice.reducer;

export type SignRoomError = 'GENERIC_ERROR';

export const retrieveBundleUrlForSignature = createAsyncThunk<
  string,
  { bundleId: string; token?: string },
  AsyncThunkConfig<SignRoomError>
>('retrieve-bundle-url-for-signature', async (val, thunkApi) => {
  try {
    return await signatureAreaRepository.retrieveBundleUrlForSignature(
      val.bundleId,
      thunkApi,
      val.token
    );
  } catch (error) {
    console.error(error);
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

export const selectSignRoomState = (state: RootState): SignRoomState =>
  state.data?.signRoom;

export const messagesForSignRoomError = (
  error: SignRoomError
): ErrorMessages => {
  switch (error) {
    case 'GENERIC_ERROR':
    default:
      return {
        title: commonErrorMessages.errorTitleDefault,
        description: commonErrorMessages.errorDescriptionDefault,
      };
  }
};
